/**
 * Add from the field — the client side of `/api/field-adds/*` (server:
 * services/fieldAdds.ts). Approved mockup:
 * https://claude.ai/artifact/DYgYcRWa5K5pw8tBURp7fG
 *
 * Saves keep working with no signal: a photo or file is queued as the same
 * multipart upload the Job Files card queues (`/api/attachments/upload` with
 * `fieldTarget`), and a note is queued as its JSON post. Both replay on
 * reconnect through the outbox.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queueOfflineAction } from "@/lib/offline-sync";

export type FieldTarget = "job" | "device" | "location" | "customer";
export type FieldAddKind = "note" | "photo" | "file";

export interface FieldAddItem {
    kind: FieldAddKind;
    id: string;
    target: FieldTarget;
    body: string | null;
    fileName: string | null;
    mimeType: string | null;
    authorUserId: string | null;
    authorName: string | null;
    createdAt: string;
    jobId: string | null;
    jobNumber: string | null;
    assetId: string | null;
    deviceLabel: string | null;
    deviceSerial: string | null;
    deviceLocation: string | null;
    siteId: string | null;
    siteName: string | null;
    customerId: string | null;
    customerName: string | null;
    needsOffice: boolean;
    readAt: string | null;
    readByName: string | null;
    canChange: boolean;
}

export interface PickerDevice {
    assetId: string;
    label: string;
    serial: string | null;
    location: string | null;
}

export interface FieldAddJobView {
    on: boolean;
    jobNumber?: string | null;
    isOffice?: boolean;
    isHelper?: boolean;
    targets?: FieldTarget[];
    devices?: PickerDevice[];
    thisJob?: FieldAddItem[];
    location?: FieldAddItem[];
    customer?: FieldAddItem[];
    devicesNotes?: FieldAddItem[];
}

export const fieldAddJobKey = (jobId: string) => ["field-adds", "job", jobId] as const;

/** The iPad's one read. `on: false` (or an error) hides every field-add control. */
export function useFieldAddJob(jobId: string | null | undefined) {
    return useQuery({
        queryKey: fieldAddJobKey(jobId ?? ""),
        queryFn: () => api.get<FieldAddJobView>(`/api/field-adds/jobs/${jobId}`),
        enabled: !!jobId,
        staleTime: 30_000,
        retry: false,
    });
}

export interface FieldAddSwitch {
    on: boolean;
    isOffice: boolean;
}

/** Office pages: is it on for me (and am I the office)? */
export function useFieldAddSwitch() {
    return useQuery({
        queryKey: ["field-adds", "switch"],
        queryFn: () => api.get<FieldAddSwitch>("/api/field-adds/switch"),
        staleTime: 60_000,
        retry: false,
    });
}

export interface SaveInput {
    jobId: string;
    kind: FieldAddKind;
    target: FieldTarget;
    assetId: string | null;
    needsOffice: boolean;
    /** note */
    body?: string;
    /** photo: a normalized data URL. file: the picked file. */
    dataUrl?: string;
    file?: File;
}

/** What came back: the saved item (online), or null when it was queued on this iPad. */
export interface SaveResult {
    queued: boolean;
    id: string | null;
    /** "notes" | "attachments", for Move it. */
    kindPath: "notes" | "attachments";
}

function dataUrlToFile(dataUrl: string, name: string): File {
    const comma = dataUrl.indexOf(",");
    const mime = dataUrl.slice(5, comma).split(";")[0] || "image/jpeg";
    const bin = atob(dataUrl.slice(comma + 1));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new File([bytes], name, { type: mime });
}

function readAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
}

export async function saveFieldAdd(input: SaveInput, isOnline: boolean): Promise<SaveResult> {
    if (input.kind === "note") {
        const body = {
            body: (input.body ?? "").trim(),
            target: input.target,
            assetId: input.assetId,
            needsOffice: input.needsOffice,
        };
        const url = `/api/field-adds/jobs/${input.jobId}/notes`;
        if (!isOnline) {
            await queueOfflineAction({ url, method: "POST", body });
            return { queued: true, id: null, kindPath: "notes" };
        }
        const res = await api.post<{ item: FieldAddItem | null }>(url, body);
        return { queued: false, id: res.item?.id ?? null, kindPath: "notes" };
    }

    const isPhoto = input.kind === "photo";
    const fileName = isPhoto
        ? `field-photo.${input.dataUrl?.includes("image/png") ? "png" : "jpg"}`
        : (input.file?.name ?? "field-file");
    const category = isPhoto || input.file?.type.startsWith("image/") ? "photo" : "document";
    const fields: Record<string, string | null> = {
        category,
        jobId: input.jobId,
        fieldTarget: input.target,
        assetId: input.target === "device" ? input.assetId : null,
        needsOffice: input.needsOffice ? "true" : "false",
    };
    if (!isOnline) {
        const dataUrl = isPhoto ? input.dataUrl! : await readAsDataUrl(input.file!);
        await queueOfflineAction({
            url: "/api/attachments/upload",
            method: "POST",
            body: null,
            fileUpload: { fileField: "file", dataUrl, fileName, fields },
        });
        return { queued: true, id: null, kindPath: "attachments" };
    }
    const fd = new FormData();
    fd.append("file", isPhoto ? dataUrlToFile(input.dataUrl!, fileName) : input.file!, fileName);
    for (const [k, v] of Object.entries(fields)) if (v != null) fd.append(k, v);
    const res = await api.postForm<{ attachment?: { id: string } }>("/api/attachments/upload", fd);
    return { queued: false, id: res.attachment?.id ?? null, kindPath: "attachments" };
}

export function moveFieldAdd(
    kindPath: "notes" | "attachments",
    id: string,
    target: FieldTarget,
    assetId: string | null,
) {
    return api.patch(`/api/field-adds/${kindPath}/${id}`, { target, assetId });
}

export function removeFieldAdd(kindPath: "notes" | "attachments", id: string) {
    return api.delete(`/api/field-adds/${kindPath}/${id}`);
}

export function kindPathOf(item: Pick<FieldAddItem, "kind">): "notes" | "attachments" {
    return item.kind === "note" ? "notes" : "attachments";
}

/** The 10 MB cap the upload route enforces. */
export const FIELD_ADD_MAX_BYTES = 10 * 1024 * 1024;
